import { CURRENCIES, type CurrencyInfo } from '@splitfiesta/shared';

// The currency table, the symbol lookup and the currency → locale mapping
// live in `@splitfiesta/shared` (apps/api's `moduleResolution: node` cannot
// read this package's `exports` subpaths, and shared must not import back
// into i18n); re-exported so this package's public API is unchanged.
export {
  CURRENCIES,
  CURRENCY_LOCALES,
  getCurrencyLocale,
  getCurrencySymbol,
} from '@splitfiesta/shared';
export type { CurrencyInfo } from '@splitfiesta/shared';

export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale?: string,
  options?: Intl.NumberFormatOptions
): string {
  const currencyInfo = CURRENCIES[currency];

  try {
    const formatOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency,
      minimumFractionDigits: currencyInfo?.decimalDigits ?? 2,
      maximumFractionDigits: currencyInfo?.decimalDigits ?? 2,
      ...options,
    };

    return new Intl.NumberFormat(locale, formatOptions).format(amount);
  } catch (_error) {
    // Fallback to manual formatting
    const symbol =
      currencyInfo?.symbolNative || currencyInfo?.symbol || currency;
    const decimals = currencyInfo?.decimalDigits ?? 2;
    const formattedAmount = amount.toFixed(decimals);

    // Check if currency should be placed after amount (like EUR in some locales)
    if (currency === 'EUR' && locale?.startsWith('de')) {
      return `${formattedAmount} ${symbol}`;
    }

    return `${symbol}${formattedAmount}`;
  }
}

export function getCurrencyInfo(currency: string): CurrencyInfo | undefined {
  return CURRENCIES[currency];
}

export function getSupportedCurrencies(): string[] {
  return Object.keys(CURRENCIES);
}

export function isCurrencySupported(currency: string): boolean {
  return currency in CURRENCIES;
}
